import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCampaignPerformance } from '@/hooks/ml';
import { useDrillDown } from './DrillDownContext';

interface Row {
  campaign_id: number;
  campaign_name: string | null;
  cbo_name: string | null;
  program_name: string | null;
  engagement_count: number;
  total_sms_attempts: number;
  recipient_count: number;
  delivered_count: number;
  reply_count: number;
  click_count: number;
  delivery_rate_pct: number | null;
  reply_rate_pct: number | null;
  click_rate_pct: number | null;
  total_cost: number | null;
  created_date: string | null;
}

function Bar({ pct, color }: { pct: number; color: string }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-16 rounded-full bg-gray-200 overflow-hidden inline-block align-middle ml-2">
      <div className={`h-full ${color}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function fmtPct(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}

export const CampaignPerformanceCard: React.FC = () => {
  const { drillCboId } = useDrillDown();
  const { data, isLoading } = useCampaignPerformance(500, drillCboId);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const r = (data?.campaigns ?? []) as unknown as Row[];
    const q = query.trim().toLowerCase();
    if (!q) return r;
    return r.filter(
      (x) =>
        String(x.campaign_id).includes(q) ||
        (x.campaign_name ?? '').toLowerCase().includes(q) ||
        (x.cbo_name ?? '').toLowerCase().includes(q) ||
        (x.program_name ?? '').toLowerCase().includes(q),
    );
  }, [data, query]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          Campaign performance{' '}
          <span className="text-xs font-normal text-gray-500">({rows.length})</span>
        </CardTitle>
        <div className="relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Filter by campaign, CBO, program…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-md border border-gray-200 bg-white pl-7 pr-2 py-1 text-xs w-64"
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-gray-500 py-4">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-500 py-4">
            {query ? 'No campaigns match your filter.' : 'No campaigns with activity yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[32rem]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3">Campaign</th>
                  <th className="py-2 pr-3">CBO</th>
                  <th className="py-2 pr-3">Program</th>
                  <th className="py-2 pr-3 text-right">Recipients</th>
                  <th className="py-2 pr-3 text-right">Delivered</th>
                  <th className="py-2 pr-3 text-right">Replies</th>
                  <th className="py-2 pr-3 text-right">Delivery %</th>
                  <th className="py-2 pr-3 text-right">Reply %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.campaign_id} className="border-b border-gray-100 last:border-0">
                    <td className="py-1.5 pr-3 font-medium text-gray-900">
                      {r.campaign_name || `#${r.campaign_id}`}
                    </td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.cbo_name || '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{r.program_name || '—'}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {(Number(r.recipient_count) || 0).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {(Number(r.delivered_count) || 0).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {(Number(r.reply_count) || 0).toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {fmtPct(r.delivery_rate_pct)}
                      {r.delivery_rate_pct != null && <Bar pct={r.delivery_rate_pct} color="bg-blue-500" />}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {fmtPct(r.reply_rate_pct)}
                      {r.reply_rate_pct != null && r.reply_rate_pct > 0 && (
                        <Bar pct={r.reply_rate_pct} color="bg-emerald-500" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
